import React from 'react';
import { DefaultButton, Icon, IconButton, Label, Stack, Text, TextField, Toggle, TooltipHost } from '@fluentui/react';
import { ILayout, ILayoutColumn, ILayoutElement, ILayoutRow } from '../layout';
import LayoutElementCell, { PALETTE } from './LayoutElementCell';
import { IDragPayload, IElementDropTarget, LayoutDndContext, dropAfter, isLayoutDrag, moveInArray, setDragPayload } from './layoutDnd';

// AC-949 / S3.1 — Layout editor scaffolding.
//
// The structural editor for a header/footer grid `layout`: add / remove / reorder rows and columns
// and set column track widths. Region-agnostic — it edits an `ILayout` and reports changes via
// onChange; the parent stores it as the `layout` sub-key of headerConfig / footerConfig. Element
// cells are shown as a read-only summary here; the element picker + per-element config land in S3.2.

interface IProps {
    layout?: ILayout;
    onChange: (layout: ILayout | undefined) => void;
}

let elSeq = 0;
const genElementId = (type: string): string => `${type}-${(elSeq++).toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;

// A width value round-trips as a number (px) for a pure integer, else the raw string (auto / 1fr / 25%).
const parseWidth = (v?: string): number | string | undefined => {
    const t = (v ?? '').trim();
    if (!t) return undefined;
    return /^\d+$/.test(t) ? Number(t) : t;
};

const StorefrontLayoutEditor: React.FC<IProps> = ({ layout, onChange }) => {
    const rows = layout?.rows ?? [];

    // AC-963 / S6.1 + AC-964 / S6.2 — drag state (held here since dataTransfer is read-protected during
    // dragover) and the current insertion indicators for rows and columns.
    const [drag, setDrag] = React.useState<IDragPayload | null>(null);
    const [overRow, setOverRow] = React.useState<{ index: number; after: boolean } | null>(null);
    const [overCol, setOverCol] = React.useState<{ row: number; index: number; after: boolean } | null>(null);
    const [overEl, setOverEl] = React.useState<IElementDropTarget | null>(null);
    const clearDrag = () => { setDrag(null); setOverRow(null); setOverCol(null); setOverEl(null); };

    // AC-964 / S6.2 — move an element, possibly to a different column/row.
    const moveElementTo = (from: { row: number; col: number; el: number }, to: { row: number; col: number; insertAt: number }) => {
        const next = rows.map((r) => ({ ...r, columns: (r.columns ?? []).map((c) => ({ ...c, elements: [...(c.elements ?? [])] })) }));
        const [moved] = next[from.row].columns[from.col].elements.splice(from.el, 1);
        if (!moved) return;
        const adjusted = from.row === to.row && from.col === to.col && from.el < to.insertAt ? to.insertAt - 1 : to.insertAt;
        next[to.row].columns[to.col].elements.splice(adjusted, 0, moved);
        commitRows(next);
    };

    const dndCtx = React.useMemo(() => ({
        drag,
        over: overEl,
        startElementDrag: (e: React.DragEvent, loc: { row: number; col: number; el: number }) => {
            const payload: IDragPayload = { kind: 'element' as const, rowIndex: loc.row, colIndex: loc.col, elIndex: loc.el };
            setDrag(payload); setDragPayload(e, payload);
        },
        setOverElement: setOverEl,
        dropElement: (target: { row: number; col: number; insertAt: number }) => {
            if (drag?.kind === 'element' && drag.rowIndex != null && drag.colIndex != null && drag.elIndex != null) {
                moveElementTo({ row: drag.rowIndex, col: drag.colIndex, el: drag.elIndex }, target);
            }
            clearDrag();
        },
        addElementAt: (target: { row: number; col: number; insertAt: number }, elementType: string) => {
            const next = rows.map((r) => ({ ...r, columns: (r.columns ?? []).map((c) => ({ ...c, elements: [...(c.elements ?? [])] })) }));
            next[target.row].columns[target.col].elements.splice(target.insertAt, 0, { id: genElementId(elementType), type: elementType as ILayoutElement['type'], props: {} });
            commitRows(next);
            clearDrag();
        },
        endDrag: clearDrag,
    }), [drag, overEl, rows]);

    // AC-964 / S6.2 — move a column, possibly to a different row. Splice out of its source row's columns,
    // then insert into the target row at the drop index (removal-shift-aware only when same row).
    const moveColumnTo = (from: { row: number; index: number }, toRow: number, insertAt: number) => {
        const next = rows.map((r) => ({ ...r, columns: [...(r.columns ?? [])] }));
        const [moved] = next[from.row].columns.splice(from.index, 1);
        if (!moved) return;
        const adjusted = from.row === toRow && from.index < insertAt ? insertAt - 1 : insertAt;
        next[toRow].columns.splice(adjusted, 0, moved);
        commitRows(next);
    };

    // Removing the last row clears the layout entirely (-> legacy rendering), matching the fail-safe gate.
    const commitRows = (next: ILayoutRow[]) => onChange(next.length ? { ...layout, rows: next } : undefined);
    const setRow = (ri: number, patch: Partial<ILayoutRow>) => commitRows(rows.map((r, i) => (i === ri ? { ...r, ...patch } : r)));
    const setCols = (ri: number, cols: ILayoutColumn[]) => setRow(ri, { columns: cols });

    const addRow = () => commitRows([...rows, { columns: [{ elements: [] }] }]);
    const deleteRow = (ri: number) => commitRows(rows.filter((_, i) => i !== ri));
    const moveRow = (ri: number, dir: -1 | 1) => {
        const j = ri + dir;
        if (j < 0 || j >= rows.length) return;
        const next = [...rows];
        [next[ri], next[j]] = [next[j], next[ri]];
        commitRows(next);
    };

    const cols = (ri: number) => rows[ri].columns ?? [];
    const addCol = (ri: number) => setCols(ri, [...cols(ri), { elements: [] }]);
    const deleteCol = (ri: number, ci: number) => setCols(ri, cols(ri).filter((_, i) => i !== ci));
    const moveCol = (ri: number, ci: number, dir: -1 | 1) => {
        const list = [...cols(ri)];
        const j = ci + dir;
        if (j < 0 || j >= list.length) return;
        [list[ci], list[j]] = [list[j], list[ci]];
        setCols(ri, list);
    };
    const setColWidth = (ri: number, ci: number, width?: string) =>
        setCols(ri, cols(ri).map((c, i) => (i === ci ? { ...c, width: parseWidth(width) } : c)));
    const setColElements = (ri: number, ci: number, elements: ILayoutElement[]) =>
        setCols(ri, cols(ri).map((c, i) => (i === ci ? { ...c, elements } : c)));
    const setColHideOnMobile = (ri: number, ci: number, hide: boolean) =>
        setCols(ri, cols(ri).map((c, i) => (i === ci ? { ...c, hideOnMobile: hide || undefined } : c)));

    return (
        <LayoutDndContext.Provider value={dndCtx}>
        <Stack tokens={{ childrenGap: 12 }}>
            <Text variant='small' styles={{ root: { color: '#605e5c' } }}>
                Build the header/footer as rows of columns. Each cell holds one or more elements
                (drag them, or use the buttons). Column widths accept px (e.g. 120), <code>auto</code>,
                <code> 1fr</code>, or a percentage.
            </Text>

            {rows.length > 0 && (
                <div>
                    <div style={{ fontSize: 11, color: '#605e5c', marginBottom: 4 }}>Drag an element into a cell, or use a cell&rsquo;s “Add element” button.</div>
                    <Stack horizontal wrap tokens={{ childrenGap: 6 }}>
                        {PALETTE.map((p) => (
                            <span
                                key={p.type}
                                className='sf-layout-grip'
                                draggable
                                onDragStart={(e) => { const payload: IDragPayload = { kind: 'new-element', elementType: p.type }; setDrag(payload); setDragPayload(e, payload); }}
                                onDragEnd={clearDrag}
                                title={`Drag to add: ${p.label}`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid #d2d0ce', borderRadius: 14, padding: '2px 10px', fontSize: 12, background: '#ffffff', color: '#323130' }}
                            >
                                <Icon iconName={p.icon} styles={{ root: { fontSize: 12 } }} />{p.label}
                            </span>
                        ))}
                    </Stack>
                </div>
            )}

            {rows.length === 0 && (
                <Text styles={{ root: { color: '#605e5c' } }}>No layout yet. Add a row to start building a grid.</Text>
            )}

            {rows.map((row, ri) => (
                <div
                    key={ri}
                    style={{ position: 'relative', border: '1px solid #e1dfdd', borderRadius: 8, padding: 10, background: drag?.kind === 'row' && overRow?.index === ri ? '#eef4e0' : '#faf9f8', opacity: drag?.kind === 'row' && drag.rowIndex === ri ? 0.5 : 1 }}
                    onDragOver={(e) => { if (drag?.kind !== 'row' || !isLayoutDrag(e)) return; e.preventDefault(); const after = dropAfter(e, e.currentTarget, true); if (overRow?.index !== ri || overRow.after !== after) setOverRow({ index: ri, after }); }}
                    onDrop={(e) => { if (drag?.kind !== 'row' || drag.rowIndex == null) return; e.preventDefault(); commitRows(moveInArray(rows, drag.rowIndex, ri + (dropAfter(e, e.currentTarget, true) ? 1 : 0))); clearDrag(); }}
                >
                    {overRow?.index === ri && (
                        <div style={{ position: 'absolute', left: 6, right: 6, height: 3, borderRadius: 2, background: '#789c2f', zIndex: 2, top: overRow.after ? undefined : -2, bottom: overRow.after ? -2 : undefined }} />
                    )}
                    <Stack horizontal horizontalAlign='space-between' verticalAlign='center' style={{ marginBottom: 8 }}>
                        <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 4 }}>
                            <span
                                className='sf-layout-grip'
                                draggable
                                onDragStart={(e) => { setDrag({ kind: 'row', rowIndex: ri }); setDragPayload(e, { kind: 'row', rowIndex: ri }); }}
                                onDragEnd={clearDrag}
                                title='Drag to reorder row (or use the arrows)'
                                aria-hidden='true'
                                style={{ display: 'inline-flex', alignItems: 'center', color: '#8a8886' }}
                            >
                                <Icon iconName='GripperDotsVertical' />
                            </span>
                            <Label styles={{ root: { padding: 0 } }}>Row {ri + 1}</Label>
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 2 }}>
                            <TooltipHost content='Add column'><IconButton iconProps={{ iconName: 'Add' }} ariaLabel='Add column' onClick={() => addCol(ri)} /></TooltipHost>
                            <IconButton iconProps={{ iconName: 'ChevronUpSmall' }} ariaLabel='Move row up' disabled={ri === 0} onClick={() => moveRow(ri, -1)} />
                            <IconButton iconProps={{ iconName: 'ChevronDownSmall' }} ariaLabel='Move row down' disabled={ri === rows.length - 1} onClick={() => moveRow(ri, 1)} />
                            <IconButton iconProps={{ iconName: 'Delete' }} ariaLabel='Delete row' onClick={() => deleteRow(ri)} />
                        </Stack>
                    </Stack>

                    <Stack horizontal wrap tokens={{ childrenGap: 8 }}>
                        {cols(ri).length === 0 && (
                            <div
                                onDragOver={(e) => { if (drag?.kind !== 'column' || !isLayoutDrag(e)) return; e.preventDefault(); if (overCol?.row !== ri) setOverCol({ row: ri, index: 0, after: false }); }}
                                onDrop={(e) => { if (drag?.kind !== 'column' || drag.rowIndex == null || drag.colIndex == null) return; e.preventDefault(); moveColumnTo({ row: drag.rowIndex, index: drag.colIndex }, ri, 0); clearDrag(); }}
                                style={{ padding: 8, borderRadius: 6, border: `1px dashed ${overCol?.row === ri ? '#789c2f' : 'transparent'}` }}
                            >
                                <Text variant='small' styles={{ root: { color: '#a19f9d' } }}>No columns. Use + to add one{drag?.kind === 'column' ? ', or drop a column here' : ''}.</Text>
                            </div>
                        )}
                        {cols(ri).map((col, ci) => (
                            <div
                                key={ci}
                                style={{ position: 'relative', border: '1px dashed #c8c6c4', borderRadius: 6, padding: 8, minWidth: 210, background: drag?.kind === 'column' && overCol?.row === ri && overCol.index === ci ? '#f3f7ea' : '#ffffff', opacity: drag?.kind === 'column' && drag.rowIndex === ri && drag.colIndex === ci ? 0.5 : 1 }}
                                onDragOver={(e) => { if (drag?.kind !== 'column' || !isLayoutDrag(e)) return; e.preventDefault(); const after = dropAfter(e, e.currentTarget, false); if (overCol?.row !== ri || overCol.index !== ci || overCol.after !== after) setOverCol({ row: ri, index: ci, after }); }}
                                onDrop={(e) => { if (drag?.kind !== 'column' || drag.rowIndex == null || drag.colIndex == null) return; e.preventDefault(); e.stopPropagation(); moveColumnTo({ row: drag.rowIndex, index: drag.colIndex }, ri, ci + (dropAfter(e, e.currentTarget, false) ? 1 : 0)); clearDrag(); }}
                            >
                                {overCol?.row === ri && overCol.index === ci && (
                                    <div style={{ position: 'absolute', top: 4, bottom: 4, width: 3, borderRadius: 2, background: '#789c2f', zIndex: 2, left: overCol.after ? undefined : -5, right: overCol.after ? -5 : undefined }} />
                                )}
                                <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
                                    <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 2 }}>
                                        <span
                                            className='sf-layout-grip'
                                            draggable
                                            onDragStart={(e) => { setDrag({ kind: 'column', rowIndex: ri, colIndex: ci }); setDragPayload(e, { kind: 'column', rowIndex: ri, colIndex: ci }); }}
                                            onDragEnd={clearDrag}
                                            title='Drag to move column (or use the arrows)'
                                            aria-hidden='true'
                                            style={{ display: 'inline-flex', alignItems: 'center', color: '#8a8886' }}
                                        >
                                            <Icon iconName='GripperDotsVertical' />
                                        </span>
                                        <Text variant='small' styles={{ root: { color: '#605e5c' } }}>Col {ci + 1}</Text>
                                    </Stack>
                                    <Stack horizontal tokens={{ childrenGap: 0 }}>
                                        <IconButton iconProps={{ iconName: 'ChevronLeftSmall' }} ariaLabel='Move column left' disabled={ci === 0} onClick={() => moveCol(ri, ci, -1)} styles={{ root: { height: 24 } }} />
                                        <IconButton iconProps={{ iconName: 'ChevronRightSmall' }} ariaLabel='Move column right' disabled={ci === cols(ri).length - 1} onClick={() => moveCol(ri, ci, 1)} styles={{ root: { height: 24 } }} />
                                        <IconButton iconProps={{ iconName: 'Delete' }} ariaLabel='Delete column' onClick={() => deleteCol(ri, ci)} styles={{ root: { height: 24 } }} />
                                    </Stack>
                                </Stack>
                                <TextField
                                    label='Width'
                                    placeholder='auto'
                                    value={col.width === undefined ? '' : String(col.width)}
                                    onChange={(_, v) => setColWidth(ri, ci, v)}
                                    styles={{ root: { width: 110 } }}
                                />
                                <Toggle
                                    label='Hide on mobile'
                                    inlineLabel
                                    checked={!!col.hideOnMobile}
                                    onChange={(_, c) => setColHideOnMobile(ri, ci, !!c)}
                                    styles={{ root: { margin: '6px 0 0' }, label: { fontSize: 11 } }}
                                />
                                <div style={{ marginTop: 8 }}>
                                    <LayoutElementCell elements={col.elements ?? []} onChange={(els) => setColElements(ri, ci, els)} location={{ row: ri, col: ci }} />
                                </div>
                            </div>
                        ))}
                    </Stack>
                </div>
            ))}

            <Stack horizontal>
                <DefaultButton iconProps={{ iconName: 'Add' }} text='Add row' onClick={addRow} />
            </Stack>
        </Stack>
        </LayoutDndContext.Provider>
    );
};

export default StorefrontLayoutEditor;
